/**
 * Zod schemas for validating inputs at every transport boundary (REST, WS, SSE).
 * The same schemas are imported on the client to validate before sending,
 * giving a single source of truth for the wire contract.
 */
import { z } from 'zod';
import { PROJECT_THEME_IDS } from './themes.js';
export const anchorSchema = z.object({
    version: z.literal(1),
    textFragment: z.string().optional(),
    cssPath: z.string().optional(),
    xpath: z.string().optional(),
    containerSelector: z.string().optional(),
    startOffset: z.number().int().nonnegative().optional(),
    endOffset: z.number().int().nonnegative().optional(),
    prefix: z.string().optional(),
    suffix: z.string().optional(),
    exact: z.string().optional(),
    elementPicker: z.boolean().optional(),
    elementId: z.string().optional(),
});
export const projectKindSchema = z.enum(['git', 'live']);
export const roleSchema = z.enum(['owner', 'editor', 'commenter', 'viewer']);
export const platformRoleSchema = z.enum(['superadmin', 'none']);
export const versionKindSchema = z.enum(['commit', 'content_hash']);
export const commentStatusSchema = z.enum(['open', 'resolved', 'misaligned']);
export const normalizeRulesSchema = z.object({
    stripSelectors: z.array(z.string()).optional(),
    collapseWhitespace: z.boolean().optional(),
});
export const projectSettingsSchema = z.object({
    homepageUrl: z.string().url().optional(),
    allowedOrigins: z.array(z.string()).optional(),
    oauth: z
        .object({
        github: z.object({ clientId: z.string(), clientSecret: z.string() }).optional(),
        google: z.object({ clientId: z.string(), clientSecret: z.string() }).optional(),
    })
        .optional(),
    branding: z
        .object({
        displayName: z.string().optional(),
        logoUrl: z.string().url().optional(),
    })
        .optional(),
    appearance: z
        .object({
        /** Six-digit hex with leading '#'. Looser than a full CSS-color
         *  validator since the picker only emits hex; widen if other
         *  formats become valid here. */
        accentColor: z
            .string()
            .regex(/^#[0-9a-fA-F]{6}$/)
            .optional(),
    })
        .optional(),
    policies: z
        .object({
        publicRead: z.boolean().optional(),
        requireAuthToComment: z.boolean().optional(),
        publicComments: z.boolean().optional(),
        openEnrollment: z.boolean().optional(),
    })
        .optional(),
    ui: z
        .object({
        widgetMode: z.enum(['docked', 'mini']).optional(),
        widgetCorner: z
            .enum(['lower-right', 'lower-left', 'upper-right', 'upper-left'])
            .optional(),
        toolbarPosition: z.enum(['top', 'bottom']).optional(),
        sidebarPosition: z.enum(['left', 'right', 'top', 'bottom']).optional(),
        /** @deprecated — see `sidebarPosition`. Validates so old configs
         *  round-trip without rejection. */
        sidebarSide: z.enum(['left', 'right']).optional(),
        sidebarDisplayMode: z.enum(['flyout', 'in-page']).optional(),
        theme: z.enum(PROJECT_THEME_IDS).optional(),
        bubblesDefault: z.boolean().optional(),
        allowInlineCommenting: z.boolean().optional(),
        tooltipDelayMs: z.number().int().min(0).max(2000).optional(),
        bubbleFollowAnimations: z.boolean().optional(),
        // CSS z-index is signed int32. Allow the full positive range so
        // admins can pin the bubble layer wherever it fits their site's
        // stacking — including very low values for host pages with
        // their own high-priority modals.
        bubbleZIndex: z.number().int().min(0).max(2147483647).optional(),
        signInMode: z.enum(['auto', 'popup', 'redirect']).optional(),
    })
        .optional(),
    behavior: z
        .object({
        enabled: z.boolean().optional(),
        autoDiscover: z.boolean().optional(),
        autoDeleteCompletedTasks: z.boolean().optional(),
        transport: z.enum(['auto', 'ws', 'sse', 'rest']).optional(),
    })
        .optional(),
    /**
     * Connected Sources (git repos + external integrations) per-project
     * enable + permission toggles. Defaults all-on per
     * docs/plans/external-integrations.md §17.2.
     */
    connectedSources: z
        .object({
        enabled: z.boolean().optional(),
        providers: z
            .record(z.string(), z.object({
            enabled: z.boolean().optional(),
            allowMembers: z.boolean().optional(),
        }))
            .optional(),
    })
        .optional(),
    dashboard: z
        .object({
        showComments: z.boolean().optional(),
        showChanges: z.boolean().optional(),
    })
        .optional(),
    /**
     * Repository-sync options. Active per-project; only meaningful when
     * at least one Repository row is connected (otherwise these fields
     * are ignored). The toggles are deliberately conservative defaults
     * (everything off) — admins opt in.
     *
     * - `autoSync` — on managed-doc load, fetch the connected repo and
     *   reconcile the on-disk page content into the live DB. Trades a
     *   small first-load latency for "always-fresh from repo."
     * - `autoVersion` — when a managed doc is saved, create a new
     *   Version row (and a new media-asset row for uploaded files)
     *   instead of overwriting. Uses more storage but preserves
     *   history; off by default.
     * - `autoCommit` — when a managed doc is saved, commit the new
     *   content into the connected repo. Path + branch follow the
     *   `commitPath` / `commitBranch` settings below.
     * - `commitBranch` — branch to commit / push to. Blank = use the
     *   repository's current `defaultBranch`. If the branch doesn't
     *   exist yet, it's created from the default branch.
     * - `commitPath` — directory inside the repo where managed-page
     *   files land. Default `.pageloop/pages`; each page is written
     *   as `<commitPath>/<page-id>.json`. Leading slash optional.
     */
    repository: z
        .object({
        autoSync: z.boolean().optional(),
        autoVersion: z.boolean().optional(),
        autoCommit: z.boolean().optional(),
        commitBranch: z.string().max(120).optional(),
        commitPath: z.string().max(240).optional(),
        /** Author identity used for auto-commits when the acting user
         *  doesn't have an authenticated git identity yet. Blank ->
         *  falls back to the acting user's display name + email. */
        commitAuthorName: z.string().max(120).optional(),
        commitAuthorEmail: z.string().email().max(240).optional(),
    })
        .optional(),
    normalize: normalizeRulesSchema.optional(),
    notifications: z
        .object({
        fromAddress: z.string().email().optional(),
        smtpUrl: z.string().optional(),
    })
        .optional(),
    moderation: z
        .object({
        profanityFilter: z.enum(['off', 'redact', 'block']).optional(),
        extraWords: z.array(z.string().min(1).max(80)).max(200).optional(),
    })
        .optional(),
    cleanup: z
        .discriminatedUnion('mode', [
        z.object({ mode: z.literal('off') }),
        z.object({
            mode: z.literal('older-than'),
            // 1 hour minimum so a typo can't wipe everything written in the
            // last minute. Operators who really want a tighter window can
            // change this guard.
            maxAgeHours: z.number().int().min(1).max(8760 /* 1y */),
            auditOnCleanup: z.boolean().optional(),
        }),
        z.object({
            mode: z.literal('keep-newest'),
            limit: z.number().int().min(0).max(100_000),
            auditOnCleanup: z.boolean().optional(),
        }),
        z.object({
            mode: z.literal('wipe-all-every'),
            intervalHours: z.number().int().min(1).max(8760),
            lastWipedAt: z.string().optional(),
            auditOnCleanup: z.boolean().optional(),
        }),
    ])
        .optional(),
    /** Live chat feature toggle + per-scope opt-outs. When `enabled`
     *  is false (or the key is missing — preserves legacy projects),
     *  every chat RPC + WebSocket join returns CHAT_DISABLED. Per-
     *  scope flags let an org enable project-wide chat without
     *  exposing the per-page side channel, etc. */
    chat: z
        .object({
        enabled: z.boolean().optional(),
        /** Scopes still active when `enabled: true`. Empty array =
         *  all three. Lets project owners enable org-wide chat
         *  without unlocking the chattier page-level channel. */
        scopes: z
            .array(z.enum(['org', 'project', 'page']))
            .optional(),
    })
        .optional(),
});
// --- Command / Query input schemas ---
export const registerPageInputSchema = z.object({
    projectId: z.string(),
    sourceKind: projectKindSchema,
    sourceId: z.string(),
    key: z.string().min(1),
    title: z.string().nullable().optional(),
});
export const getPageByKeyInputSchema = z.object({
    projectId: z.string(),
    key: z.string().min(1),
});
// --- Page indexing + managed docs ----------------------------------
/** Sort keys for `pages.list`. The repo translates each into an ORDER BY. */
export const pagesSortKeySchema = z.enum([
    'lastCommentedAt',
    'createdAt',
    'lastUpdatedAt',
    'commentCount',
    'title',
]);
export const sortDirSchema = z.enum(['asc', 'desc']);
/** Source-kind filter: combines real page kinds with the synthetic
 *  `managed` value so the admin UI can split tabs without re-deriving
 *  the predicate from the key prefix. */
export const pageSourceFilterSchema = z.enum(['all', 'live', 'git', 'managed']);
export const listPagesInputSchema = z.object({
    projectId: z.string(),
    sort: pagesSortKeySchema.optional(),
    dir: sortDirSchema.optional(),
    /** Substring match on title/key (case-insensitive). Trimmed and
     *  empty-checked server-side — pass any string. */
    q: z.string().max(120).optional(),
    source: pageSourceFilterSchema.optional(),
    limit: z.number().int().positive().max(200).optional(),
    /** Opaque cursor returned by a previous call. */
    cursor: z.string().nullable().optional(),
    /** Default false. When true, rows with `settings.displayVisibility`
     *  of 'hidden' or 'disabled' are included. The admin Pages list flips
     *  this on so editors can manage hidden pages; default false matches
     *  the visitor-facing semantics. */
    includeHidden: z.boolean().optional(),
    /** When set to a string id, only pages whose `groupId` matches.
     *  Pass `null` for "pages at the project root" (groupId IS NULL).
     *  Folder navigation on the project dashboard wires this. */
    groupId: z.string().nullable().optional(),
    /** When true, return only pages that have at least one comment or task.
     *  Powers the widget's "Pages with comments" dropdown. */
    hasComments: z.boolean().optional(),
    /** Which tasks the per-page `counts.tasks` reflects (and which pages
     *  `hasComments` keeps): `incomplete` (default; open/unresolved),
     *  `complete` (resolved), or `all`. */
    taskType: z.enum(['incomplete', 'complete', 'all']).optional(),
});
/** "Track this page" — idempotent register that also flips the
 *  `manuallyAdded` flag. Called by the widget admin menu. */
export const trackCurrentPageInputSchema = z.object({
    projectId: z.string(),
    key: z.string().min(1),
    title: z.string().nullable().optional(),
    sourceKind: projectKindSchema.optional(),
    sourceId: z.string().optional(),
});
/** Per-page visibility. `inherit` falls through to project policy
 *  (today's default). The others override the project's publicRead
 *  for this single page. */
export const pageVisibilitySchema = z.enum(['inherit', 'public', 'org', 'private']);
export const setPageVisibilityInputSchema = z.object({
    pageId: z.string(),
    visibility: pageVisibilitySchema,
});
/** Display-visibility (separate from access-control `visibility`). Drives
 *  the admin's "Show / Hide / Disable" tri-toggle. See `PageSettings`. */
export const pageDisplayVisibilitySchema = z.enum(['visible', 'hidden', 'disabled']);
export const pageSettingsSchema = z.object({
    displayVisibility: pageDisplayVisibilitySchema,
    commentingEnabled: z.boolean(),
    publicCommentingEnabled: z.boolean(),
});
/** Partial-update patch for `pages.updateSettings`. Every field optional
 *  so the admin UI can flip one toggle without round-tripping the rest. */
export const pageSettingsPatchSchema = pageSettingsSchema.partial();
export const updatePageSettingsInputSchema = z.object({
    projectId: z.string(),
    pageId: z.string(),
    patch: pageSettingsPatchSchema,
});
/** Soft-delete a page (set `trashed_at`). Cascades happen on
 *  `pages.purge`, not here. Project-editor gated. */
export const deletePageInputSchema = z.object({
    projectId: z.string(),
    pageId: z.string(),
});
/** Soft-delete many pages at once (admin bulk "Edit → Delete"). Same
 *  trash semantics + Project-editor gate as `pages.delete`; capped so a
 *  single call can't sweep an unbounded set. */
export const deleteManyPagesInputSchema = z.object({
    projectId: z.string(),
    pageIds: z.array(z.string()).min(1).max(500),
});
/** Restore a page from the trash. Project-editor gated. */
export const restorePageInputSchema = z.object({
    projectId: z.string(),
    pageId: z.string(),
});
/** Permanently remove a page + all its comments. Only callable
 *  against trashed rows. Project-manage gated. */
export const purgePageInputSchema = z.object({
    projectId: z.string(),
    pageId: z.string(),
});
/** List pages currently in the trash. */
export const listTrashedPagesInputSchema = z.object({
    projectId: z.string(),
});
/** Move a page into a group (or to the root). Project-editor gated. */
export const movePageToGroupInputSchema = z.object({
    projectId: z.string(),
    pageId: z.string(),
    groupId: z.string().nullable(),
});
/** Download a page's content as a file. Server dispatches per
 *  TrackedPage.origin — text managed docs come back inline, media
 *  assets and (future) cloud-backed payloads come back as signed
 *  URLs. Repo-tracked files are read from the working clone and
 *  returned inline. */
export const downloadPageInputSchema = z.object({
    pageId: z.string().min(1),
});
/** Resolve a batch of markdown link hrefs to in-app page URLs (when
 *  the link points at another tracked page) or to remote-repo URLs
 *  (when the source page is repo-tracked and the file lives in the
 *  same git repo). Used by the admin's markdown renderer to upgrade
 *  inline `[label](./other.md)` references into navigable links. */
export const resolveLinksInputSchema = z.object({
    sourcePageId: z.string().min(1),
    hrefs: z.array(z.string().min(1)).min(1).max(500),
});
// ============================================================
// Live chat
// ============================================================
/** Scope discriminator shared by every chat RPC. */
export const chatScopeSchema = z.enum(['org', 'project', 'page']);
/** Message body cap (chars). Keeps the WS frame sane and discourages
 *  pasting entire documents into a chat channel; long-form belongs
 *  on the page itself. */
const CHAT_BODY_MAX = 4000;
export const chatHistoryInputSchema = z.object({
    scope: chatScopeSchema,
    scopeId: z.string().min(1),
    /** Pagination: return rows with `created_at < before`. ISO-8601. */
    before: z.string().optional(),
    limit: z.number().int().min(1).max(200).optional(),
});
export const chatSendInputSchema = z.object({
    scope: chatScopeSchema,
    scopeId: z.string().min(1),
    body: z.string().trim().min(1).max(CHAT_BODY_MAX),
    /** Display name to attribute the message to when the caller is
     *  anonymous. Server collision-resolves by appending an integer
     *  suffix when the requested name is already live in the room. */
    guestName: z.string().trim().min(1).max(60).optional(),
});
export const chatPresenceInputSchema = z.object({
    scope: chatScopeSchema,
    scopeId: z.string().min(1),
});
export const chatTypingInputSchema = z.object({
    scope: chatScopeSchema,
    scopeId: z.string().min(1),
    /** Display name when the typist is anonymous. Ignored when the
     *  caller is authenticated (server uses the real displayName/email
     *  instead). */
    guestName: z.string().trim().min(1).max(60).optional(),
});
export const chatMarkReadInputSchema = z.object({
    scope: chatScopeSchema,
    scopeId: z.string().min(1),
    /** Optional ISO timestamp; server clamps to no-later-than-now. When
     *  omitted the cursor moves to the current server time. */
    lastReadAt: z.string().optional(),
});
export const chatReadStateInputSchema = z.object({
    scope: chatScopeSchema,
    scopeId: z.string().min(1),
});
/** Moderation: clear a room's entire transcript. Same (scope, scopeId)
 *  shape as the other chat RPCs; server gates on `comment.moderate`. */
export const chatClearInputSchema = z.object({
    scope: chatScopeSchema,
    scopeId: z.string().min(1),
});
export const githubAppStatusInputSchema = z.object({
    projectId: z.string().min(1),
});
/** Page groups — folders that own pages and can nest via parent_group_id. */
export const listPageGroupsInputSchema = z.object({
    projectId: z.string(),
    /** Default false. When true, trashed groups + their pages flow
     *  through. Admin-only at the handler. */
    includeTrashed: z.boolean().optional(),
});
export const createPageGroupInputSchema = z.object({
    projectId: z.string(),
    name: z.string().min(1).max(120),
    /** Null = create at the root. */
    parentGroupId: z.string().nullable().optional(),
});
export const renamePageGroupInputSchema = z.object({
    projectId: z.string(),
    groupId: z.string(),
    name: z.string().min(1).max(120),
});
export const movePageGroupInputSchema = z.object({
    projectId: z.string(),
    groupId: z.string(),
    /** Null = move to root. Cannot equal `groupId` (self-parent). */
    parentGroupId: z.string().nullable(),
});
/** Trash a group with one of two semantics. `ungroup` moves children
 *  up one level (parent's parent for groups, parent group for pages)
 *  and is non-destructive. `trash-children` marks all descendants
 *  trashed too — the admin UI must confirm with the child count. */
export const trashPageGroupInputSchema = z.object({
    projectId: z.string(),
    groupId: z.string(),
    mode: z.enum(['ungroup', 'trash-children']),
});
// --- Share tokens ---------------------------------------------------
// `read` = view the bound page's comments; `comment` = + create comments/
// replies; `task` = read + flip task completion (resolve/reopen) on the bound
// page — the scoped grant an "agent link" carries so an AI agent can read
// unresolved tasks and mark them done WITHOUT a logged-in identity. `task`
// deliberately does NOT allow editing comment bodies or general moderation.
export const shareTokenPermissionSchema = z.enum(['read', 'comment', 'task']);
export const createShareTokenInputSchema = z.object({
    pageId: z.string(),
    permission: shareTokenPermissionSchema.default('read'),
    /** TTL in seconds. Server clamps to a sensible max (default 30d).
     *  Operators picking a longer TTL can re-issue. */
    expiresInSeconds: z.number().int().positive().max(60 * 60 * 24 * 30 * 3).optional(),
});
export const revokeShareTokenInputSchema = z.object({
    tokenId: z.string(),
});
export const listShareTokensInputSchema = z.object({
    pageId: z.string(),
});
export const managedDocBodyKindSchema = z.enum(['plain', 'md', 'rich', 'media']);
// --- Media / storage --------------------------------------------------
/**
 * The wire-side input for `media.getReadUrl`. The asset id implies
 * the project + org — server enforces the cross-tenant scope before
 * minting a URL.
 *
 * Variants:
 *   - 'original' — always the bytes the user uploaded.
 *   - 'processed' — the derived HTML variant (when ready), original
 *     otherwise.
 *   - omitted (default) — same as 'processed': prefer the derived
 *     variant when available so the dashboard always renders the
 *     "best" view.
 */
export const mediaVariantSchema = z.enum(['original', 'processed']);
export const mediaGetReadUrlInputSchema = z.object({
    assetId: z.string(),
    /** Force a download with the original filename. */
    asAttachment: z.boolean().optional(),
    variant: mediaVariantSchema.optional(),
});
export const mediaHeadInputSchema = z.object({
    assetId: z.string(),
});
export const mediaListInputSchema = z.object({
    projectId: z.string(),
});
export const mediaDeleteInputSchema = z.object({
    assetId: z.string(),
});
export const createManagedDocInputSchema = z.object({
    projectId: z.string(),
    title: z.string().min(1).max(200),
    bodyKind: managedDocBodyKindSchema.optional(),
    bodyText: z.string().max(2_000_000).optional(),
    repoPath: z.string().max(1024).nullable().optional(),
    /** When set, the doc wraps an already-uploaded media asset and
     *  bodyKind is forced to 'media'. The asset must belong to the
     *  same project — server enforces. */
    mediaAssetId: z.string().nullable().optional(),
    /** Optional page-group to file the wrapping tracked-page under.
     *  Null / undefined → land at the project root. The group must
     *  belong to the same project; server enforces. */
    groupId: z.string().nullable().optional(),
});
export const updateManagedDocInputSchema = z.object({
    docId: z.string(),
    title: z.string().min(1).max(200).optional(),
    bodyKind: managedDocBodyKindSchema.optional(),
    bodyText: z.string().max(2_000_000).optional(),
    repoPath: z.string().max(1024).nullable().optional(),
    /** Optimistic-concurrency token — the value the caller loaded. */
    expectedVersion: z.number().int().min(1),
});
export const getManagedDocInputSchema = z.object({
    docId: z.string(),
});
export const listManagedDocsInputSchema = z.object({
    projectId: z.string(),
});
export const deleteManagedDocInputSchema = z.object({
    docId: z.string(),
});
export const createCommentInputSchema = z.object({
    trackedPageId: z.string(),
    /** Omit / null for a "general" (non-anchored) comment. */
    anchor: anchorSchema.nullish(),
    bodyMd: z.string().min(1).max(10_000),
    versionId: z.string().nullable().optional(),
    /** Display name for guest commenters (when project allows public comments). */
    guestName: z.string().min(1).max(60).optional(),
});
export const updateCommentInputSchema = z.object({
    commentId: z.string(),
    bodyMd: z.string().min(1).max(10_000).optional(),
    status: commentStatusSchema.optional(),
    /** Re-anchor an existing comment to a freshly-picked element (e.g. the
     *  original target was hidden/removed so the comment went "no anchor").
     *  `null` detaches it into a general (anchorless) comment. Gated to the
     *  author or an editor/owner. */
    anchor: anchorSchema.nullish(),
});
export const deleteCommentInputSchema = z.object({
    commentId: z.string(),
});
export const listRepliesInputSchema = z.object({
    commentId: z.string(),
});
/** Reaction "kind" was originally a small enum ('+1', 'heart', etc.)
 *  but the widget now ships a full emoji picker, so the kind field
 *  stores the raw emoji string. The legacy enum values are still
 *  accepted (back-compat for rows written before the widening) — the
 *  client maps them to their original glyphs at render time. */
export const reactionKindSchema = z.string().trim().min(1).max(64);
export const reactionTargetKindSchema = z.enum(['comment', 'reply', 'chat-message']);
export const listReactionsInputSchema = z.object({
    targetKind: reactionTargetKindSchema,
    targetId: z.string(),
});
export const toggleReactionInputSchema = z.object({
    targetKind: reactionTargetKindSchema,
    targetId: z.string(),
    kind: reactionKindSchema,
});
export const bulkDeleteCommentsInputSchema = z.object({
    trackedPageId: z.string(),
});
export const createReplyInputSchema = z.object({
    commentId: z.string(),
    bodyMd: z.string().min(1).max(10_000),
    /** Display name for guest repliers (when project allows public comments). */
    guestName: z.string().min(1).max(60).optional(),
});
export const updateReplyInputSchema = z.object({
    replyId: z.string(),
    bodyMd: z.string().min(1).max(10_000),
});
export const deleteReplyInputSchema = z.object({
    replyId: z.string(),
});
/** Task completion filter for the list endpoints. */
export const taskStatusSchema = z.enum(['resolved', 'unresolved']);
export const listCommentsInputSchema = z.object({
    trackedPageId: z.string(),
    status: commentStatusSchema.optional(),
    /** When true, return only comments promoted to tasks (resolved or not). */
    task: z.boolean().optional(),
    /** Narrow tasks to a completion state. Implies `task: true`. */
    taskStatus: taskStatusSchema.optional(),
    /** Return only the single comment with this id (still page-scoped). Lets an
     *  agent link target one specific comment/task. */
    commentId: z.string().optional(),
});
/** Promote/demote a comment to/from a task, or toggle its completion.
 *  All fields optional patches: `isTask` flips the task flag (false also
 *  clears completion); `resolved` toggles a task's completion. */
export const setCommentTaskInputSchema = z.object({
    commentId: z.string(),
    isTask: z.boolean().optional(),
    resolved: z.boolean().optional(),
});
export const bootstrapInputSchema = z.object({
    projectId: z.string(),
    pageKey: z.string().optional(),
});
export const createProjectInputSchema = z.object({
    slug: z.string().min(1),
    name: z.string().min(1),
    ownerEmail: z.string().email(),
    settings: projectSettingsSchema.optional(),
});
/**
 * Mutable project fields. Slug is intentionally not patchable here:
 * it's the public identifier baked into client-side PageLoop.init,
 * the IDB cache key, and any URL the operator has shared. Renaming
 * the display name is safe; renaming the slug isn't.
 */
export const updateProjectInputSchema = z.object({
    projectId: z.string(),
    name: z.string().min(1).max(120).optional(),
    settings: projectSettingsSchema.optional(),
});
/**
 * Move a project from its current org to another. Caller must be
 * admin+ on the source AND a member of the target. Target plan must
 * have room (project-count cap). Project memberships travel; org
 * member rosters do not — adopt the project, not the people.
 */
export const transferProjectInputSchema = z.object({
    projectId: z.string(),
    targetOrganizationId: z.string(),
});
/**
 * Inputs for the project soft-delete lifecycle. `delete` and
 * `restore` flip `trashed_at`; `purge` permanently removes the row
 * (gated server-side to only allow purge on projects that are
 * already trashed). All three accept just the projectId — the
 * server resolves the org scope from the project's row.
 */
export const deleteProjectInputSchema = z.object({
    projectId: z.string(),
});
export const restoreProjectInputSchema = z.object({
    projectId: z.string(),
});
export const purgeProjectInputSchema = z.object({
    projectId: z.string(),
});
/** On-demand cleanup-sweep input. Just a project id — policy lives on
 *  the project's own settings. */
export const runCleanupNowInputSchema = z.object({
    projectId: z.string(),
});
export const memberRoleSchema = z.enum(['owner', 'editor', 'commenter', 'viewer']);
export const listMembersInputSchema = z.object({
    projectId: z.string(),
});
export const addMemberInputSchema = z.object({
    projectId: z.string(),
    email: z.string().email(),
    role: memberRoleSchema.default('commenter'),
    /** Send the invite email after creating the membership. Treated as
     *  true when omitted; only explicit `false` suppresses the email.
     *  Useful for bulk seeding or when the invitee is already aware
     *  out-of-band. */
    sendInvite: z.boolean().optional(),
});
export const removeMemberInputSchema = z.object({
    projectId: z.string(),
    userId: z.string(),
});
export const updateMemberRoleInputSchema = z.object({
    projectId: z.string(),
    userId: z.string(),
    role: memberRoleSchema,
});
/** Shared shape for the dashboard's "recent X for project" RPCs. */
export const recentForProjectInputSchema = z.object({
    projectId: z.string(),
    limit: z.number().int().positive().max(100).optional(),
});
/** Paginated + hydrated variant of `recentForProjectInputSchema`. The
 *  cursor is an opaque base64-encoded offset (see CommentsHandler). */
export const recentForProjectFeedInputSchema = z.object({
    projectId: z.string(),
    limit: z.number().int().positive().max(100).optional(),
    cursor: z.string().nullable().optional(),
});
// --- Plan / billing primitives -------------------------------------
/** Catalog-driven plan id. Stays in sync with the union in entities.ts. */
export const planIdSchema = z.enum([
    'free',
    'indie',
    'indie_annual',
    'pro',
    'pro_annual',
    'team',
    'team_annual',
    'enterprise',
    'unlimited',
]);
export const subscriptionStatusSchema = z.enum(['trialing', 'active', 'past_due', 'cancelled']);
// --- Organisation schemas ------------------------------------------
/**
 * Slug rules: lowercase alphanumerics + single hyphens between segments;
 * no leading/trailing hyphen; 2-40 chars. Strict enough to fit cleanly
 * in future subdomain mapping (`org-slug.annotr.dev`) and URL paths.
 */
export const orgSlugSchema = z
    .string()
    .min(2)
    .max(40)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, {
    message: 'lowercase letters, digits, single hyphens (no leading/trailing)',
});
export const orgRoleSchema = z.enum(['owner', 'admin', 'member']);
export const createOrganizationInputSchema = z.object({
    /** Display name. Slug is derived if not supplied. */
    name: z.string().min(1).max(80),
    /** Optional override; auto-derived from name + uniqueness check otherwise. */
    slug: orgSlugSchema.optional(),
    /** Personal orgs are the auto-created one on signup; team is user-driven. */
    kind: z.enum(['personal', 'team']).default('team'),
});
export const updateOrganizationInputSchema = z.object({
    organizationId: z.string(),
    name: z.string().min(1).max(80).optional(),
    /** Slug *can* be patched on Org (unlike Project.slug) since Org isn't
     *  baked into client snippets — only into account URLs. UI should
     *  warn that this breaks any pretty URL the user has shared. */
    slug: orgSlugSchema.optional(),
});
export const inviteOrgMemberInputSchema = z.object({
    organizationId: z.string(),
    email: z.string().email(),
    role: orgRoleSchema.default('member'),
});
export const removeOrgMemberInputSchema = z.object({
    organizationId: z.string(),
    userId: z.string(),
});
/** Self-service "leave" — same shape as `removeOrgMember` minus the
 *  `userId` field; the server uses `ctx.user.id` so the caller can
 *  only leave on their own behalf. Avoids the "user A removes
 *  user B via the leave endpoint" footgun. */
export const leaveOrgInputSchema = z.object({
    organizationId: z.string(),
});
/** Self-service profile patch. Subset of the User fields the user is
 *  allowed to mutate from the admin UI; the server ignores anything
 *  not listed here. Email / oauth fields are immutable from this
 *  surface (would require re-auth flow). */
export const updateProfileInputSchema = z.object({
    displayName: z.string().trim().min(1).max(80).nullable().optional(),
    avatarUrl: z.string().trim().max(2048).nullable().optional(),
});
export const updateOrgMemberRoleInputSchema = z.object({
    organizationId: z.string(),
    userId: z.string(),
    role: orgRoleSchema,
});
export const listOrgMembersInputSchema = z.object({
    organizationId: z.string(),
});
export const getOrganizationInputSchema = z.object({
    organizationId: z.string(),
});
/** Exchange an invite token for org-membership acceptance. The token is
 *  a short-lived JWT minted by `orgs.members.invite` and embedded in
 *  the magic-link email. */
export const acceptOrgInviteInputSchema = z.object({
    token: z.string().min(1),
});
// --- Signup --------------------------------------------------------
/**
 * Signup input. Pre-filled from OAuth profile on the callback page;
 * the user can edit the org name/slug before confirming. `email`
 * comes from the OAuth provider and is the seed for the User row.
 */
export const completeSignupInputSchema = z.object({
    /** Display name for the auto-created personal org. */
    organizationName: z.string().min(1).max(80),
    /** Optional explicit slug; otherwise derived from name. */
    organizationSlug: orgSlugSchema.optional(),
    /** Whether to seed a welcome project + sample comment. Default true. */
    seedWelcomeProject: z.boolean().default(true),
});
// --- Local email/password auth -------------------------------------
/** New-account registration. `displayName` optional (defaults to the
 *  email local-part server-side when omitted). Password floor is 8
 *  chars; the UI mirrors this. */
export const registerInputSchema = z.object({
    email: z.string().trim().email().max(320),
    password: z.string().min(8).max(200),
    displayName: z.string().trim().min(1).max(120).optional(),
});
/** Email/password sign-in. Password min is 1 here (not 8) so a legacy
 *  short password still authenticates; the floor only applies to new
 *  registrations. */
export const loginInputSchema = z.object({
    email: z.string().trim().email().max(320),
    password: z.string().min(1).max(200),
});
/** Superadmin user deletion (platform-wide). Hard-deletes the user row
 *  + cascades; gated to superadmin at the handler. */
export const deleteUserInputSchema = z.object({
    userId: z.string().min(1),
});
// --- Billing -------------------------------------------------------
export const startCheckoutInputSchema = z.object({
    organizationId: z.string(),
    plan: planIdSchema,
    /** Return URL after payment success. */
    successUrl: z.string().url(),
    /** Return URL on cancellation/back. */
    cancelUrl: z.string().url(),
});
export const openPortalInputSchema = z.object({
    organizationId: z.string(),
    returnUrl: z.string().url(),
});
export const cancelSubscriptionInputSchema = z.object({
    organizationId: z.string(),
});
export const getSubscriptionInputSchema = z.object({
    organizationId: z.string(),
});
export const createSetupIntentInputSchema = z.object({
    organizationId: z.string(),
});
export const changePlanInputSchema = z.object({
    organizationId: z.string(),
    plan: planIdSchema,
});
export const paymentMethodActionInputSchema = z.object({
    organizationId: z.string(),
    paymentMethodId: z.string(),
});
export const feedbackSubmitInputSchema = z.object({
    /** The feedback / issue / suggestion body. */
    message: z.string().min(1).max(5000),
    /** Submitter name — used for anonymous visitors; ignored for signed-in
     *  users (their identity comes from the session). */
    name: z.string().max(200).optional(),
    /** Submitter email — same rule as `name`. */
    email: z.string().max(320).optional(),
});
/**
 * Audit log read input. `before` is an ISO timestamp cursor — pass the
 * last-row's `createdAt` to fetch the next page. `action` accepts an
 * exact code or a `prefix.` to filter by domain (e.g. `project.`).
 */
export const listAuditEventsInputSchema = z.object({
    organizationId: z.string(),
    limit: z.number().int().positive().max(200).optional(),
    before: z.string().nullable().optional(),
    action: z.string().min(1).max(80).optional(),
});
/** CSV export — same plan-gate as listAuditEvents. `since` lets the
 *  caller pick a window; absent = "everything we have." Hard cap of
 *  10k rows; bigger exports go through an offline job (post-MVP). */
export const exportAuditEventsInputSchema = z.object({
    organizationId: z.string(),
    since: z.string().nullable().optional(),
    action: z.string().min(1).max(80).optional(),
});
/** Render a transactional email template with operator-supplied
 *  variables. Superadmin-only. Used by the admin /admin/email-preview
 *  tool so operators can see what's actually shipping before turning
 *  Resend on (or while tweaking copy). */
export const previewEmailInputSchema = z.object({
    template: z.enum(['welcome', 'orgInvite', 'trialEnding', 'paymentFailed']),
    /** Render-time overrides — recipientName, organizationName, etc.
     *  Unknown keys are ignored. Shape is per-template; the handler
     *  fills sensible defaults for missing keys. */
    vars: z.record(z.string(), z.string()).optional(),
});
/**
 * Repo-tree browse: directory listing inside a connected repo. `path`
 * is relative to repo root; empty string = root. `branch` defaults
 * server-side to the repo's default branch. The path is normalised
 * by stripping leading/trailing slashes — keeps the SimpleGitAdapter
 * call site's `ls-tree` invocation deterministic regardless of how
 * the caller serialised it.
 */
export const listRepoTreeInputSchema = z.object({
    repoId: z.string().min(1),
    path: z
        .string()
        .transform((s) => s.replace(/^\/+|\/+$/g, ''))
        .default(''),
    branch: z.string().optional(),
});
/** List branches in a connected repo. No path; just the repo id +
 *  policy gate via the RPC handler. */
export const listRepoBranchesInputSchema = z.object({
    repoId: z.string().min(1),
});
/** Repo-file fetch: full bytes at the tip of `branch`. Path
 *  normalisation matches `listRepoTreeInputSchema`. */
export const fetchRepoFileInputSchema = z.object({
    repoId: z.string().min(1),
    path: z.string().min(1).transform((s) => s.replace(/^\/+|\/+$/g, '')),
    branch: z.string().optional(),
});
/** Bulk register inputs (preview + commit share most fields). The
 *  preview RPC computes conflicts; the commit RPC applies them.
 *  `items` may include both files and directories — the server walks
 *  the directories recursively (respecting includeExts / excludeExts
 *  / excludePaths). */
const repoRegisterItemSchema = z.object({
    path: z.string().min(1).transform((s) => s.replace(/^\/+|\/+$/g, '')),
    kind: z.enum(['file', 'dir']),
});
const repoRegisterBaseSchema = z.object({
    repoId: z.string().min(1),
    projectId: z.string().min(1),
    items: z.array(repoRegisterItemSchema).min(1).max(2000),
    flatten: z.boolean().optional(),
    includeExts: z.array(z.string().min(1)).optional(),
    excludeExts: z.array(z.string().min(1)).optional(),
    excludePaths: z.array(z.string().min(1)).optional(),
    /** Exact path-segment names to skip. Distinct from `excludePaths`
     *  (substring match) — `excludeNames: ['md']` drops files/folders
     *  literally named `md`, not every path containing the letters
     *  `md`. Drives the unified exclude-input UX in the browser modal. */
    excludeNames: z.array(z.string().min(1)).optional(),
    branch: z.string().optional(),
});
export const previewRegisterRepoFilesInputSchema = repoRegisterBaseSchema;
export const registerRepoFilesInputSchema = repoRegisterBaseSchema.extend({
    conflictActions: z
        .record(z.string(), z.enum(['overwrite', 'skip', 'rename']))
        .optional(),
    defaultConflict: z.enum(['overwrite', 'skip', 'rename']).optional(),
});
/** Edit a tracked-repo-file. Body cap is 50 MB base64-encoded —
 *  realistic doc-style files are well under that, and the cap keeps
 *  one bad client from filling memory with a huge payload. */
export const updateRepoFileInputSchema = z.object({
    pageId: z.string().min(1),
    contentBase64: z.string().max(50 * 1024 * 1024),
    expectedEtag: z.string().nullable(),
    commitMessage: z.string().max(500).optional(),
});
/* User settings + subscriptions + push — input schemas wired into
 * Application.SCHEMAS so the rpc routing layer validates them at the
 * boundary. Group-scoped settings updates carry an opaque `patch`
 * object — the SettingsHandler picks the matching schema from
 * SETTINGS_GROUPS and re-validates it server-side before merging. */
export const settingsGetInputSchema = z.object({
    groupId: z.string().optional(),
});
export const settingsUpdateInputSchema = z.object({
    groupId: z.string().min(1),
    patch: z.record(z.string(), z.unknown()),
});
export const settingsResetInputSchema = z.object({
    groupId: z.string().min(1),
});
export const subscriptionPageInputSchema = z.object({
    pageId: z.string().min(1),
});
export const pushSubscribeInputSchema = z.object({
    endpoint: z.string().url().min(1),
    keys: z.object({
        p256dh: z.string().min(1),
        auth: z.string().min(1),
    }),
    userAgent: z.string().max(500).optional(),
});
export const pushUnsubscribeInputSchema = z.object({
    endpoint: z.string().url().min(1),
});
// --- External integrations + Connected Sources --------------------
const externalProviderIdSchema = z.enum([
    'notion',
    'evernote',
    'obsidian',
    'onenote',
    'google-docs',
]);
export const integrationsListInputSchema = z.object({
    provider: externalProviderIdSchema.optional(),
});
export const integrationsStartConnectInputSchema = z.object({
    provider: externalProviderIdSchema,
    returnUrl: z.string().optional(),
});
export const integrationsMintPushTokenInputSchema = z.object({
    provider: externalProviderIdSchema,
    label: z.string().max(80).optional(),
});
export const integrationsRevokePushTokenInputSchema = z.object({
    tokenId: z.string().min(1),
});
export const integrationsDisconnectInputSchema = z.object({
    integrationId: z.string().min(1),
});
export const integrationsListAccessibleDocsInputSchema = z.object({
    integrationId: z.string().min(1),
    cursor: z.string().optional(),
});
export const integrationsImportInputSchema = z.object({
    integrationId: z.string().min(1),
    projectId: z.string().min(1),
    remoteIds: z.array(z.string().min(1)).min(1).max(200),
});
export const sourcesListForProjectInputSchema = z.object({
    projectId: z.string().min(1),
});
// --- Bubble colours -----------------------------------------------
export const colorsResolveForCurrentUserInputSchema = z.object({
    projectId: z.string().min(1),
});
export const colorsListForProjectInputSchema = z.object({
    projectId: z.string().min(1),
});
//# sourceMappingURL=schemas.js.map